import { DateHighLighted } from "app/modules/tickets/drawer/components/type";
import { TSelectedPackageItem } from "app/modules/tickets/drawer/package-flow/DateTimeComponent/type";
import { DatePackageAvailable, PackageInfo, RouteOptimizationCfg, Worker } from "app/modules/tickets/models/OrderDrawerModel";
import { TimeSlot, TimeSlotPackage } from "../common/models";
import { TSelectedItem } from "./components/Form";
import moment from 'moment'
import { DEFAULT_WORKER, Languages, SlotDisplayType, SlotDisplayTypeValue } from '../common/constants'

type SlotWithDisplayWindow = {
    start: number;
    end: number;
    visitReminderStart?: number;
    visitReminderEnd?: number;
};

/** Normalize cfg object — API may use camelCase or PascalCase property names. */
export const normalizeRouteOptimizationCfg = (raw: unknown): RouteOptimizationCfg | null => {
    if (!raw || typeof raw !== 'object') return null
    const o = raw as Record<string, unknown>
    const slotDisplayType = o.slotDisplayType ?? o.SlotDisplayType
    if (slotDisplayType === undefined || slotDisplayType === null) return null

    return {
        id: Number(o.id ?? o.Id ?? 0),
        dispatchId: Number(o.dispatchId ?? o.DispatchId ?? 0),
        slotDisplayType: slotDisplayType as number | string,
        minutesBefore: Number(o.minutesBefore ?? o.MinutesBefore ?? 0),
        minutesAfter: Number(o.minutesAfter ?? o.MinutesAfter ?? 0),
        createdAt: (o.createdAt ?? o.CreatedAt) as string | undefined,
        updatedAt: (o.updatedAt ?? o.UpdatedAt) as string | undefined,
    }
}

/** API may return cfg on root, preSelectSP, preSelectProvider — camelCase or PascalCase. */
export const pickRouteOptimizationCfg = (source: unknown): RouteOptimizationCfg | null => {
    if (!source || typeof source !== 'object') return null
    const root = source as Record<string, unknown>

    const candidates: unknown[] = [
        root.routeOptimizationCfg,
        root.RouteOptimizationCfg,
        (root.preSelectSP as Record<string, unknown> | undefined)?.routeOptimizationCfg,
        (root.preSelectSP as Record<string, unknown> | undefined)?.RouteOptimizationCfg,
        (root.preSelectProvider as Record<string, unknown> | undefined)?.routeOptimizationCfg,
        (root.preSelectProvider as Record<string, unknown> | undefined)?.RouteOptimizationCfg,
    ]

    for (const raw of candidates) {
        const normalized = normalizeRouteOptimizationCfg(raw)
        if (normalized) return normalized
    }

    return null
};

/** Pick worker/bus entry from get-reschedule-ticket `workers` array. */
export const resolveRescheduleWorkerFromList = (
    workers: unknown[] | undefined,
    options?: { workerId?: string; spId?: string },
): Record<string, unknown> | undefined => {
    if (!workers?.length) return undefined

    const { workerId, spId } = options ?? {}

    if (workerId && workerId !== '0') {
        const byWorker = workers.find((w) => (w as { workerId?: string }).workerId === workerId)
        if (byWorker) return byWorker as Record<string, unknown>
    }

    if (spId) {
        const bySp = workers.find((w) => (w as { spId?: string }).spId === spId)
        if (bySp) return bySp as Record<string, unknown>
    }

    const baseOn = workers.find((w) => (w as { isBaseOnAvailable?: boolean }).isBaseOnAvailable)
    return (baseOn ?? workers[0]) as Record<string, unknown>
}

export const pickRouteOptimizationCfgFromWorkers = (
    workers: unknown[] | undefined,
    options?: { workerId?: string; spId?: string },
): RouteOptimizationCfg | null =>
    pickRouteOptimizationCfg(resolveRescheduleWorkerFromList(workers, options))

export const resolveVisitReminderFromWorkers = (
    workers: unknown[] | undefined,
    options?: { workerId?: string; spId?: string },
    fallback?: string | null,
): string => {
    const worker = resolveRescheduleWorkerFromList(workers, options) as { visitReminder?: string } | undefined
    return worker?.visitReminder ?? fallback ?? ''
}

export const resolveCreateOrderWorker = (
    workerId?: string,
    workerAvailable?: unknown[],
): Worker | undefined => {
    if (!workerId || workerId === DEFAULT_WORKER || !workerAvailable?.length) return undefined
    const found = workerAvailable.find(
        (w) => typeof w === 'object' && w !== null && 'workerId' in w && (w as Worker).workerId === workerId,
    )
    return found && typeof found === 'object' && 'workerId' in found ? (found as Worker) : undefined
}

export type CreateOrderSlotDisplayContext = {
    routeOptimizationCfg: RouteOptimizationCfg | null
    visitReminder: string
}

/** Delivery window cfg for create order — worker calendar uses preSelectProvider.routeOptimizationCfg from API. */
export const resolveCreateOrderSlotDisplayContext = (
    values: {
        worker?: string
        worker_available?: unknown[]
        selectedWorkerRouteOptimizationCfg?: RouteOptimizationCfg | null
        selectedWorkerVisitReminder?: string
        selectedDateRouteOptimizationCfg?: RouteOptimizationCfg | null
        selectedDateVisitReminder?: string
        preSelectSP?: { visitReminder?: string; routeOptimizationCfg?: RouteOptimizationCfg | null } | null
    },
    apiFallback?: {
        routeOptimizationCfg?: RouteOptimizationCfg | null
        visitReminder?: string | null
    },
): CreateOrderSlotDisplayContext => {
    const selectedWorker = resolveCreateOrderWorker(values.worker, values.worker_available)

    const fromDatePickApi = (): CreateOrderSlotDisplayContext | null => {
        if (apiFallback === undefined) return null
        const routeOptimizationCfg =
            apiFallback.routeOptimizationCfg !== undefined
                ? normalizeRouteOptimizationCfg(apiFallback.routeOptimizationCfg) ?? null
                : pickRouteOptimizationCfg(apiFallback) ?? null
        return {
            routeOptimizationCfg,
            visitReminder: apiFallback.visitReminder ?? '',
        }
    }

    if (!selectedWorker) {
        const apiContext = fromDatePickApi()
        if (apiContext) return apiContext

        if (
            values.worker === DEFAULT_WORKER &&
            values.selectedDateRouteOptimizationCfg !== undefined
        ) {
            return {
                routeOptimizationCfg:
                    normalizeRouteOptimizationCfg(values.selectedDateRouteOptimizationCfg) ?? null,
                visitReminder: values.selectedDateVisitReminder ?? '',
            }
        }

        return {
            routeOptimizationCfg: pickRouteOptimizationCfg(values.preSelectSP) ?? null,
            visitReminder: values.preSelectSP?.visitReminder ?? '',
        }
    }

    const apiContext = fromDatePickApi()
    if (apiContext) {
        return {
            routeOptimizationCfg: apiContext.routeOptimizationCfg,
            visitReminder:
                apiContext.visitReminder ||
                values.selectedWorkerVisitReminder ||
                selectedWorker.visitReminder ||
                '',
        }
    }

    const routeOptimizationCfg =
        normalizeRouteOptimizationCfg(values.selectedWorkerRouteOptimizationCfg) ??
        pickRouteOptimizationCfg(selectedWorker) ??
        null
    const visitReminder =
        values.selectedWorkerVisitReminder ??
        selectedWorker.visitReminder ??
        values.preSelectSP?.visitReminder ??
        ''

    return { routeOptimizationCfg, visitReminder }
}

const resolveRouteOptimizationCfg = (
    routeOptimizationCfg?: RouteOptimizationCfg | null,
): RouteOptimizationCfg | null =>
    normalizeRouteOptimizationCfg(routeOptimizationCfg) ?? null

const normalizeSlotDisplayType = (
    routeOptimizationCfg?: RouteOptimizationCfg | null,
): number | undefined => {
    const cfg = resolveRouteOptimizationCfg(routeOptimizationCfg)
    const raw = cfg?.slotDisplayType
    if (raw === undefined || raw === null) return undefined

    if (typeof raw === 'number' && Number.isFinite(raw)) return raw

    const asString = String(raw).trim()
    if (!asString) return undefined
    if (asString === SlotDisplayType.DeliveryWindow) return SlotDisplayTypeValue.DeliveryWindow
    if (asString === SlotDisplayType.ExactVisitTime) return SlotDisplayTypeValue.ExactVisitTime
    const n = Number(asString)
    return Number.isFinite(n) ? n : undefined
}

/** slotDisplayType=2: display window start = slot.start - minutesBefore, end = slot.start + minutesAfter */
export const isDeliveryWindowSlotDisplay = (
    routeOptimizationCfg?: RouteOptimizationCfg | null,
): boolean =>
    normalizeSlotDisplayType(routeOptimizationCfg) === SlotDisplayTypeValue.DeliveryWindow

/** slotDisplayType=1: exact visit — show API start/end only (ignore visitReminder window). */
export const isExactVisitSlotDisplay = (
    routeOptimizationCfg?: RouteOptimizationCfg | null,
): boolean =>
    normalizeSlotDisplayType(routeOptimizationCfg) === SlotDisplayTypeValue.ExactVisitTime

export const shouldShowDeliveryWindow = (
    routeOptimizationCfg?: RouteOptimizationCfg | null,
    isShowDeliveryTime?: boolean,
    visitReminder?: string | null,
): boolean => {
    // API explicitly returned no routeOptimizationCfg — do not fall back to legacy visitReminder window.
    if (routeOptimizationCfg === null) return false
    if (isExactVisitSlotDisplay(routeOptimizationCfg)) return false
    if (isDeliveryWindowSlotDisplay(routeOptimizationCfg)) return true
    return !!(isShowDeliveryTime && visitReminder)
};

export const applySlotDisplayWindow = <T extends { start: number; end: number }>(
    slots: T[],
    routeOptimizationCfg?: RouteOptimizationCfg | null,
    visitReminder?: string | null,
    isShowDeliveryTime?: boolean,
): (T & SlotWithDisplayWindow)[] => {
    const cfg = resolveRouteOptimizationCfg(routeOptimizationCfg)
    if (isDeliveryWindowSlotDisplay(cfg)) {
        const before = cfg!.minutesBefore ?? 0;
        const after = cfg!.minutesAfter ?? 0;
        return slots.map((el) => ({
            ...el,
            visitReminderStart: el.start - before,
            visitReminderEnd: el.start + after,
        }));
    }

    // Route optimization exact mode: never apply visitReminder delivery window.
    if (isExactVisitSlotDisplay(cfg)) {
        return slots;
    }

    // API explicitly returned no routeOptimizationCfg — show raw slot times only.
    if (routeOptimizationCfg === null) {
        return slots;
    }

    if (isShowDeliveryTime && visitReminder) {
        try {
            const { Before, After } = JSON.parse(visitReminder);
            return slots.map((el) => ({
                ...el,
                visitReminderStart: el.start - Number(Before),
                visitReminderEnd: el.start + Number(After),
            }));
        } catch {
            return slots;
        }
    }

    return slots;
};

/** Display range for a slot — same rules as create-order DateTimeVisit + applySlotDisplayWindow. */
export const resolveSlotDisplayRange = (
    start: number,
    end: number,
    routeOptimizationCfg?: RouteOptimizationCfg | null,
    visitReminder?: string | null,
    isShowDeliveryTime?: boolean,
): { startValue: number; endValue: number } => {
    if (routeOptimizationCfg === null) {
        return { startValue: start, endValue: end }
    }
    if (!shouldShowDeliveryWindow(routeOptimizationCfg, isShowDeliveryTime, visitReminder)) {
        return { startValue: start, endValue: end }
    }

    const cfg = resolveRouteOptimizationCfg(routeOptimizationCfg)
    if (isDeliveryWindowSlotDisplay(cfg)) {
        const before = cfg!.minutesBefore ?? 0
        const after = cfg!.minutesAfter ?? 0
        return {
            startValue: start - before,
            endValue: start + after,
        }
    }

    if (isShowDeliveryTime && visitReminder) {
        try {
            const { Before, After } = JSON.parse(visitReminder)
            return {
                startValue: start - Number(Before),
                endValue: start + Number(After),
            }
        } catch {
            return { startValue: start, endValue: end }
        }
    }

    return { startValue: start, endValue: end }
}

/** Display range for scheduled visit (package-details, ticket view). */
export const getScheduledVisitDisplayRange = (
    scheduledVisitDateTime: string | Date,
    scheduledVisitEndDateTime: string | Date,
    options?: {
        bufferVisitTime?: number
        routeOptimizationCfg?: RouteOptimizationCfg | null
    },
): { start: moment.Moment; end: moment.Moment } => {
    const visitStart = moment(scheduledVisitDateTime)
    const cfg = resolveRouteOptimizationCfg(options?.routeOptimizationCfg ?? null)

    if (isDeliveryWindowSlotDisplay(cfg)) {
        const before = cfg!.minutesBefore ?? 0
        const after = cfg!.minutesAfter ?? 0
        return {
            start: visitStart.clone().subtract(before, 'minutes'),
            end: visitStart.clone().add(after, 'minutes'),
        }
    }

    return {
        start: visitStart,
        end: moment(scheduledVisitEndDateTime).subtract(options?.bufferVisitTime ?? 0, 'minutes'),
    }
}

export const transformPackageInfo = (packageInfo: PackageInfo[]) => {
    return packageInfo.map(s => ({
        id: s.id,
        titleAr: s.titleAr,
        titleEn: s.titleEn,
        packagePrice: s.packagePrice,
        packagePriceWithVat: s.packagePriceWithVat,
        percentVAT: s.percentVAT,
        multiWorker: s.multiWorker,
        numberOfWeeks: s.numberOfWeeks,
        numberOfVisitPerWeek: s.numberOfVisitPerWeek,
        preSelectSP: s.preSelectSP,
        hasUnratedWorker: s.hasUnratedWorker,
        workers: s.workers,
        packageServicePriceSetId: s.packageServicePriceSetId || null,
        packageServicePriceSetDetailId: s.packageServicePriceSetDetailId || null,
        isRefund: s.isRefund,
        reScheduleLimit: s.reScheduleLimit || null,
        isShowDeliveryTime: s.isShowDeliveryTime,
        subServiceId: s.subServiceId,
        numberOfVisitRecurrence: s.numberOfVisitRecurrence
    }))
}

export const highlightDateProcess = (dayAvailable: DatePackageAvailable[], intervalDay: number, intervalWeek: number) => {
    const availableDate: DateHighLighted[] = []
    if (intervalDay === 1 && intervalWeek > 1) {
        const startDay = dayAvailable[0]
        if (startDay) {
            for (let i = 0; i < intervalWeek; i++) {
                const numberDate = new Date(startDay.dateCheck).getDate()
                availableDate.push({ date: new Date(new Date(startDay.dateCheck).setDate(numberDate + i * 7)), timeSlots: startDay.listTimes || [] })
            }
        }
    } else {
        dayAvailable.forEach(
            (day: DatePackageAvailable) =>
                day.isAvailable &&
                availableDate.push({ date: new Date(day.dateCheck), timeSlots: day.listTimes || [] })
        )
    }
    return availableDate
}

export const highlightDateSeveralVist = (selectedDates: TSelectedItem[] | undefined, choosenDate: Date) => {
    let highlightDates = new Array()
    if (selectedDates && selectedDates.length !== 0) {
        let firstDate = selectedDates[0].date
        let currentDate = choosenDate
        if (firstDate.getTime() > choosenDate.getTime()) {
            while (currentDate.getTime() < firstDate.getTime()) {
                highlightDates.push({ date: currentDate, timeSlots: [] })
                currentDate = currentDate.addDays(1)
            }
        } else {
            while (firstDate.getTime() < currentDate.getTime()) {
                highlightDates.push({ date: firstDate, timeSlots: [] })
                firstDate = firstDate.addDays(1)
            }
        }

    }
    highlightDates.push({ date: choosenDate, timeSlots: [] })
    return highlightDates
}

export const activeDateProcess = (dayAvailable: DateHighLighted[], minDate: Date) => { // Add dates after selected date in several visit case
    let arrayDates = new Array()
    let currentDate = minDate
    while (currentDate.getTime() < dayAvailable[0].date.getTime()) {
        arrayDates.push({ date: currentDate, timeSlots: [] })
        currentDate = currentDate.addDays(1)
    }

    return arrayDates.concat(dayAvailable)
}
export const transformWorkerData = (data: Worker[]) => {
    return data.map(s => {

        const startDate = s.listAvailableDay?.find(item => item.isAvailable)?.dateCheck

        return {
            workerId: s.workerId,
            spId:s.spId,
            workerNameAr: s.workerNameAr,
            workerNameEn: s.workerNameEn,
            startDate,
            isAuto: s.isAuto || false,
            personalImageUrl: s.personalImageUrl,
            listAvailableDay: s.listAvailableDay,
            isFavoriteWorker: s.isFavoriteWorker,
            isLastCompleted: s.isLastCompleted,
            isBaseOnAvailable: s.isBaseOnAvailable,
            visitReminder: s.visitReminder,
            routeOptimizationCfg: s.routeOptimizationCfg,
        }
    })
}

export const generateDateSelectedSingleVisitPerWeek = (choosenDate: Date, selectedDate: TSelectedItem[], intervalWeek: number, timeSlots: TimeSlot[] | TimeSlotPackage[] | any) => {
    let result: TSelectedItem[] = []

    let newSelectedDate: TSelectedItem[] = []
    for (let i = 0; i < intervalWeek; i++) {
        newSelectedDate.push({ date: choosenDate.addDays(7 * i), time: undefined, customerVisitDuration: 0, timeSlot: timeSlots, related: i > 0 })
    }

    if (newSelectedDate && newSelectedDate.length === 0) {
        result = newSelectedDate
    } else {
        const id = selectedDate.findIndex(e => e.date.isSame(choosenDate))
        if (id < 0) {
            result = newSelectedDate
        } else {
            result = selectedDate
        }
    }
    return result
}

export const getDateSelectedSingleVisitPerWeek = (choosenDate: Date, selectedDate: TSelectedPackageItem[], intervalWeek: number, timeSlots: TimeSlot[] | TimeSlotPackage[] | any, initTime: TimeSlotPackage | undefined) => {
    let result: TSelectedPackageItem[] = []

    let newSelectedDate: TSelectedPackageItem[] = []
    for (let i = 0; i < intervalWeek; i++) {
        newSelectedDate.push({ date: choosenDate.addDays(7 * i), time: initTime, timeSlot: timeSlots, related: i > 0 ? choosenDate : undefined })
    }

    if (newSelectedDate && newSelectedDate.length === 0) {
        result = newSelectedDate
    } else {
        const id = selectedDate.findIndex(e => e.date.isSame(choosenDate))
        if (id < 0) {
            result = newSelectedDate
        } else {
            result = selectedDate
        }
    }
    return result
}

export const highlightDateSeveralVistPackage = (firstDate: Date, choosenDate: Date) => {
    let highlightDates = new Array()
    let currentDate = choosenDate
    if (firstDate.getTime() > choosenDate.getTime()) {
        while (currentDate.getTime() < firstDate.getTime()) {
            highlightDates.push({ date: currentDate, timeSlots: [] })
            currentDate = currentDate.addDays(1)
        }
    } else {
        while (firstDate.getTime() < currentDate.getTime()) {
            highlightDates.push({ date: firstDate, timeSlots: [] })
            firstDate = firstDate.addDays(1)
        }
    }
    highlightDates.push({ date: choosenDate, timeSlots: [] })
    return highlightDates
}

export const formatDateByLocale = (date: Date | string | undefined, lang: string, format: string) => moment(date || new Date()).locale(lang === Languages.ar ? 'ar-SA' : 'en-US').format(format)
